import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Card,
  Spin,
  Tooltip,
  Modal,
  Input,
  message,
  Checkbox,
  Space,
  Drawer,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  FacebookFilled,
  GithubOutlined,
  InstagramOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { FaEye, FaPen, FaTrashAlt } from "react-icons/fa";
import { hasPermission } from "../../utils/HasPermission";
import {
  deleteCompany,
  getAllCompany,
  getAllUserAttendanceExport,
  getexportCompanies,
} from "../../utils/API";
import { RiAddBoxFill } from "react-icons/ri";
import * as XLSX from "xlsx";
import { EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import EditCompanyModal from "./Edit";
import AddCompanyModal from "./Add";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Company {
  key: string;
  companyName: string;
  email: string;
  phone: string;
  industry: string;
  companySize: string;
  keyContact: string;
  totalDeals: number;
  createdAt: string;
  updatedAt: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  revenue?: number;
  description?: string;
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
}

const CompanyTable = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [currentTask, setCurrentTask] = useState<Company | null>(null);
  const [viewCompany, setViewCompany] = useState<Company | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [initialCompanyData, setInitialCompanyData] = useState<Company | null>(
    null,
  );

  const onCloseDrawer = () => {
    setIsViewModalVisible(false);
  };

  const fetchCompanies = async (params: { page: number; search?: string }) => {
    setLoading(true);
    try {
      // In production, call actual API with search and pagination parameters
      const response = await getAllCompany(params.search, params.page);
      setCompanies(response.data);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to load companies");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies({ page, search: searchText });
  }, [page, searchText]); // Now fetchCompanies is stable

  const handleUpdate = async (values: any) => {
    console.log("Update values:", values);
  };

  const handleCreate = async (values: any) => {
    try {
      message.success("Company created successfully");
      setIsAddModalVisible(false);
      fetchCompanies({ page: 1, search: searchText });
    } catch (error) {
      message.error("Failed to create company");
    }
  };

  const handleEdit = (record: Company) => {
    setCurrentCompany(record);
    setInitialCompanyData(record); // Set initial data for edit modal
    setIsEditModalVisible(true);
    console.log("Editing company:", record);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1); // Reset to first page when searching
  };

  const handleAddCompany = () => {
    setIsAddModalVisible(true);
  };

  const handleDeleteConfirmation = (record: any) => {
    console.log("Deleting company with record:", record);
    setDeleteId(record._id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        // In production, call API to delete
        await deleteCompany(deleteId);

        message.success("Company deleted successfully");
        fetchCompanies({ page, search: searchText });
      } catch (error) {
        message.error("Error deleting company. Please try again.");
      } finally {
        setDeleteId(null);
      }
    }
  };

  const handleView = (record: Company) => {
    setViewCompany(record);
    setIsViewModalVisible(true);
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      // Fetching all companies (large limit or exportAll flag)
      const response = await getexportCompanies(""); // Ensure backend supports large limit

      if (!response.data || response.data.length === 0) {
        message.warning("No companies data to export");
        return;
      }

      const exportData = (response.data as Company[]).map((item, index) => ({
        "#": index + 1,
        "Company Name": item.companyName || "-",
        Email: item.email || "-",
        Phone: item.phone || "-",
        Industry: item.industry || "-",
        "Company Size": item.companySize || "-",
        "Key Contact": item.keyContact || "-",
        "Total Deals": item.totalDeals || 0,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Companies");

      const currentDate = dayjs().format("DD-MM-YYYY");
      const fileName = `Companies_Export_${currentDate}.xlsx`;

      XLSX.writeFile(wb, fileName);
      message.success(`Exported ${response.data.length} companies`);
    } catch (error) {
      message.error("Failed to export companies data");
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: "",
      key: "selection",
      width: 50,
      fixed: "left" as const,
      render: (_: any, record: Company) => (
        <Checkbox
          checked={selectedRows.includes(record.key)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, record.key]);
            } else {
              setSelectedRows(selectedRows.filter((key) => key !== record.key));
            }
          }}
        />
      ),
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      fixed: "left" as const,
      render: (text: string, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: 35,
              height: 35,
              borderRadius: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <img
              src={`${process.env.REACT_APP_IMAGE_URL}/companies/${record.profilePhoto || "default.png"}`}
              alt={`Logo of ${record.companyName}`}
              style={{
                width: "100%",
                borderRadius: "100%",
                height: "100%",
                objectFit: "contain",
                padding: "0px",
              }}
            />
          </div>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {record.companyName || "N/A"}
          </span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      fixed: "left" as const,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
    },
    {
      title: "Company Size",
      dataIndex: "companySize",
      key: "companySize",
    },
    {
      title: "Key Contact",
      dataIndex: "keyContact",
      key: "keyContact",
    },
    {
      title: "Total Deals",
      dataIndex: "totalDeals",
      key: "totalDeals",
      render: (deals: number) => (
        <span style={{ fontWeight: 500 }}>{deals}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Company) => (
        <div className="d-flex" style={{ gap: "8px", alignItems: "center" }}>
          {hasPermission("customers", "get") && (
            <Tooltip title="View">
              <button
                onClick={() => handleView(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 
                shadow-[0_4px_12px_rgba(59,130,246,0.3)] 
                hover:shadow-[0_6px_16px_rgba(59,130,246,0.4)] 
                active:translate-y-[1px] active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 ease-in-out group"
              >
                <EyeOutlined
                  className="group-hover:scale-110 transition-transform duration-200"
                  style={{ fontSize: "12px" }}
                />
              </button>
            </Tooltip>
          )}
          {hasPermission("customers", "update") && (
            <Tooltip title="Edit">
              <button
                onClick={() => handleEdit(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                active:translate-y-[1px] active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 ease-in-out group"
              >
                <FaPen
                  className="group-hover:scale-110 transition-transform duration-200"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
          {hasPermission("customers", "delete") && (
            <Tooltip title="Delete">
              <button
                onClick={() => handleDeleteConfirmation(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                active:translate-y-1 active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 group"
              >
                <FaTrashAlt
                  className="group-hover:scale-110 transition-transform"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <>
      <Row className="m-2" gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 style={{ fontSize: "20px", fontWeight: 600, margin: 0 }}>
            Companies
          </h2>
        </Col>

        <Col xs={24} sm={24} md={18} lg={18} xl={18}>
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "flex-end",
              height: "33px",
            }}
          >
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => handleSearch(searchText)}
              allowClear
              style={{ width: 200 }}
            />

            <Button
              type="primary"
              onClick={handleAddCompany}
              style={{ background: "#001529", height: "33px" }}
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>

            <Button
              onClick={exportToExcel}
              style={{
                background: "#001529",
                color: "white",
                border: "none",
                height: "33px",
                minWidth: "100px",
              }}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export Data"}
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Card className="w-100">
          <Col>
            <Spin spinning={loading}>
              <div
                className="scrollbar-hide custom-scroll-table"
                style={{ overflowX: "auto" }}
              >
                <Table
                  columns={columns}
                  dataSource={companies}
                  rowKey="key"
                  scroll={{ x: "max-content" }}
                  pagination={{
                    pageSize: 10,
                    current: page,
                    onChange: (pageNumber) => setPage(pageNumber),
                    showSizeChanger: false,
                    total: totalRecords,
                  }}
                />
              </div>
            </Spin>
          </Col>
        </Card>
      </Row>

      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Company?</p>
      </Modal>

      <Drawer
        placement="right"
        onClose={onCloseDrawer}
        open={isViewModalVisible}
        width={380}
        bodyStyle={{ padding: 0, backgroundColor: "#fff" }}
        headerStyle={{ display: "none" }}
      >
        {viewCompany && (
          <div className="text-sm">
            <div
              style={{ backgroundColor: "#001529" }}
              className="text-white p-4 relative"
            >
              <button
                onClick={onCloseDrawer}
                className="absolute top-2 right-2 text-white text-lg font-bold hover:text-gray-300"
              >
                &times;
              </button>

              <div className="flex items-center gap-4">
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "4px",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 24,
                      fontWeight: "bold",
                      color: "#666",
                    }}
                  >
                    {viewCompany.companyName
                      ? viewCompany.companyName.charAt(0).toUpperCase()
                      : ""}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold">
                    {viewCompany.companyName}
                  </h2>
                  <p className="text-sm">
                    {viewCompany.industry || "Industry"}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-gray-200 mt-1">
                    <span>
                      {viewCompany.updatedAt
                        ? new Date(viewCompany.updatedAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-200 mt-1">
                    <GlobalOutlined className="text-base" />
                    <span>{viewCompany.country || "-"}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 text-center mt-4">
                <div>
                  <p className="font-bold text-base">
                    {viewCompany.totalDeals || 0}
                  </p>
                  <p className="text-xs">Total Deals</p>
                </div>
                <div>
                  <p className="font-bold text-base">
                    {viewCompany.companySize || "-"}
                  </p>
                  <p className="text-xs">Company Size</p>
                </div>
                <div>
                  <p className="font-bold text-base">
                    {viewCompany.revenue
                      ? `$${viewCompany.revenue.toLocaleString()}`
                      : "-"}
                  </p>
                  <p className="text-xs">Annual Revenue</p>
                </div>
              </div>
            </div>

            <div className="bg-white text-black">
              <div className="p-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Professional Bio :
                </p>
                <p className="text-gray-700">
                  {viewCompany.description ||
                    `Information about ${viewCompany.companyName || "this company"}.`}
                </p>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              <div className="px-4 pb-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Contact Information:
                </p>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-blue-600">
                      <MailOutlined />
                    </div>
                    <span className="text-sm">
                      {viewCompany.email || "N/A"}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-green-600">
                      <PhoneOutlined />
                    </div>
                    <span className="text-sm">
                      {viewCompany.phone || "N/A"}
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-red-600">
                      <EnvironmentOutlined />
                    </div>
                    <span className="text-sm">
                      {viewCompany.address
                        ? `${viewCompany.address}, ${viewCompany.city || ""}, ${viewCompany.country || ""}`
                        : "N/A"}
                    </span>
                  </li>
                </ul>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              <div className="px-4 pb-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Key Contact:
                </p>
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "100%",
                      backgroundColor: "#f0f0f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#666",
                      }}
                    >
                      {viewCompany.keyContact
                        ? viewCompany.keyContact.charAt(0).toUpperCase()
                        : ""}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {viewCompany.keyContact || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              {viewCompany.socialLinks && (
                <div className="flex items-center gap-4 px-4 pb-4 ">
                  <span className="font-semibold text-gray-800 whitespace-nowrap">
                    Social Networks :
                  </span>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-purple-100 text-purple-600 cursor-pointer hover:scale-110 transition-transform">
                      <FacebookFilled />
                    </div>

                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-sky-100 text-sky-600 cursor-pointer hover:scale-110">
                      <TwitterOutlined />
                    </div>

                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-orange-100 text-orange-600 cursor-pointer hover:scale-110 transition-transform">
                      <InstagramOutlined />
                    </div>
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-green-100 text-green-700 cursor-pointer hover:scale-110 transition-transform">
                      <GithubOutlined />
                    </div>
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-red-100 text-red-500 cursor-pointer hover:scale-110 transition-transform">
                      <YoutubeOutlined />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {currentCompany && (
        <EditCompanyModal
          visible={isEditModalVisible}
          onCancel={() => setIsEditModalVisible(false)}
          onUpdate={handleUpdate}
          initialValues={initialCompanyData}
          onCreate={function (values: any): void {
            throw new Error("Function not implemented.");
          }}
          refreshLeads={() => fetchCompanies({ page, search: searchText })}
        />
      )}

      <AddCompanyModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onCreate={handleCreate}
        refreshLeads={() => fetchCompanies({ page, search: searchText })}
      />
    </>
  );
};

export const CONVERSATION_TABLE_COLUMNS = [
  "companyName",
  "email",
  "phone",
  "industry",
  "companySize",
  "keyContact",
  "totalDeals",
];

export default CompanyTable;
